import { useState, useRef } from 'react';
import { questionImportAPI, questionImportExportAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, 
  AlertTriangle, RefreshCw, Info, Loader2, FileText, File
} from 'lucide-react';

export default function BatchImportTab() {
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await questionImportExportAPI.getTemplate();
      const template = res.data.template;
      
      // Create CSV from template
      const headers = template.columns.join(',');
      const examples = template.example_rows.map(row => 
        template.columns.map(col => `"${row[col] || ''}"`).join(',')
      );
      
      const csv = [headers, ...examples].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-import-soal.csv';
      a.click();
      
      toast.success('Template berhasil diunduh');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Gagal mengunduh template');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - support both CSV and XLSX
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExt)) {
      toast.error('File harus berformat CSV atau Excel (.xlsx, .xls)');
      return;
    }

    try {
      setValidationErrors([]);
      let questions = [];

      if (fileExt === '.csv') {
        questions = await parseCSVFile(file);
      } else {
        // For Excel files, use a simpler approach - read as text and try to parse
        // In production, you'd use a library like xlsx
        toast.info('Untuk file Excel, harap convert ke CSV terlebih dahulu');
        return;
      }

      // Validate each question
      const errors = [];
      questions.forEach((q, idx) => {
        const rowErrors = validateQuestion(q, idx + 2); // +2 for header row and 1-based index
        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
        }
      });

      setValidationErrors(errors);
      setPreview(questions);
      
      if (errors.length > 0) {
        toast.warning(`${questions.length} soal ditemukan, ${errors.length} peringatan validasi`);
      } else {
        toast.success(`${questions.length} soal siap diimport`);
      }
    } catch (error) {
      console.error('Failed to parse file:', error);
      toast.error('Gagal membaca file: ' + error.message);
    }
  };

  // Validate a single question
  const validateQuestion = (q, rowNum) => {
    const errors = [];
    
    if (!q.text || q.text.trim().length < 10) {
      errors.push({ row: rowNum, field: 'text', message: 'Teks soal terlalu pendek (min 10 karakter)' });
    }
    
    if (!q.program) {
      errors.push({ row: rowNum, field: 'program', message: 'Program wajib diisi' });
    }
    
    if (!q.mapel) {
      errors.push({ row: rowNum, field: 'mapel', message: 'Mapel wajib diisi' });
    }
    
    // For multiple choice, check options and answer
    if (q.question_type === 'multiple_choice' || !q.question_type) {
      if (!q.option_a || !q.option_b) {
        errors.push({ row: rowNum, field: 'options', message: 'Minimal opsi A dan B wajib diisi' });
      }
      
      if (!q.correct_answer || !['A', 'B', 'C', 'D', 'E'].includes(q.correct_answer?.toUpperCase())) {
        errors.push({ row: rowNum, field: 'correct_answer', message: 'Jawaban benar (A/B/C/D/E) wajib diisi' });
      }
    }
    
    // For weighted questions (TKP), check weights
    if (q.question_type === 'multiple_choice_weighted') {
      const weights = [q.weight_a, q.weight_b, q.weight_c, q.weight_d, q.weight_e];
      const hasWeights = weights.some(w => w > 0);
      if (!hasWeights) {
        errors.push({ row: rowNum, field: 'weights', message: 'Soal TKP harus memiliki bobot (weight_a - weight_e)' });
      }
    }
    
    return errors;
  };

  // Parse CSV file
  const parseCSVFile = async (file) => {
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    
    if (lines.length < 2) {
      throw new Error('File CSV kosong atau tidak valid');
    }

    const headers = parseCSVLine(lines[0]);
    const questions = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length < 3) continue; // Skip empty or invalid rows

      const row = {};
      headers.forEach((h, idx) => {
        row[h.trim().toLowerCase()] = values[idx]?.trim() || '';
      });

      // Map to API format
      questions.push({
        text: row.text || '',
        question_type: row.question_type || 'multiple_choice',
        program: row.program || '',
        mapel: row.mapel || '',
        kategori: row.kategori || '',
        difficulty: row.difficulty || 'medium',
        option_a: row.option_a || '',
        option_b: row.option_b || '',
        option_c: row.option_c || '',
        option_d: row.option_d || '',
        option_e: row.option_e || '',
        correct_answer: (row.correct_answer || '').toUpperCase(),
        weight_a: parseInt(row.weight_a) || 0,
        weight_b: parseInt(row.weight_b) || 0,
        weight_c: parseInt(row.weight_c) || 0,
        weight_d: parseInt(row.weight_d) || 0,
        weight_e: parseInt(row.weight_e) || 0,
        explanation: row.explanation || '',
        points: parseInt(row.points) || 10,
        time_limit: parseInt(row.time_limit) || 60
      });
    }

    return questions;
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    
    return result.map(v => v.replace(/^"|"$/g, ''));
  };

  const handleImport = async () => {
    if (preview.length === 0) {
      toast.error('Tidak ada soal untuk diimport');
      return;
    }

    // Check for critical validation errors
    const criticalErrors = validationErrors.filter(e => 
      e.field === 'text' || e.field === 'program' || e.field === 'correct_answer'
    );
    
    if (criticalErrors.length > 0) {
      toast.error(`Ada ${criticalErrors.length} error kritis yang harus diperbaiki sebelum import`);
      return;
    }

    try {
      setImporting(true);
      setImportProgress(0);
      
      // Import in batches for progress tracking
      const batchSize = 50;
      const batches = [];
      for (let i = 0; i < preview.length; i += batchSize) {
        batches.push(preview.slice(i, i + batchSize));
      }
      
      let totalImported = 0;
      let allErrors = [];
      
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const res = await questionImportAPI.batchImport(batch);
        
        totalImported += res.data.imported || 0;
        if (res.data.errors?.length > 0) {
          allErrors.push(...res.data.errors);
        }
        
        // Update progress
        setImportProgress(Math.round(((i + 1) / batches.length) * 100));
      }
      
      setResult({
        imported: totalImported,
        errors: allErrors
      });
      
      if (totalImported > 0) {
        toast.success(`${totalImported} soal berhasil diimport`);
      }
      if (allErrors.length > 0) {
        toast.warning(`${allErrors.length} soal gagal diimport`);
      }
    } catch (error) {
      console.error('Failed to import:', error);
      toast.error('Gagal mengimport soal: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const clearPreview = () => {
    setPreview([]);
    setResult(null);
    setValidationErrors([]);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Import Soal Massal</h2>
          <p className="text-slate-500">Upload file CSV untuk menambahkan banyak soal sekaligus</p>
        </div>
        <Button variant="outline" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>
      </div>

      {/* Instructions */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Panduan Import:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Download template CSV terlebih dahulu</li>
              <li>Isi data soal sesuai format template</li>
              <li>Untuk soal pilihan ganda biasa, isi correct_answer (A/B/C/D/E)</li>
              <li>Untuk soal TKP/weighted, isi weight_a sampai weight_e (0-5)</li>
              <li>Upload file dan preview sebelum import</li>
            </ol>
          </div>
        </div>
      </Card>

      {/* Upload Area */}
      <Card className="p-8 border-2 border-dashed border-slate-200 text-center">
        <input 
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />
        
        <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="font-semibold text-slate-900 mb-2">Upload File CSV</h3>
        <p className="text-sm text-slate-500 mb-4">
          Pilih file CSV yang berisi data soal
        </p>
        <Button onClick={() => fileInputRef.current?.click()}>
          <Upload className="w-4 h-4 mr-2" />
          Pilih File
        </Button>
      </Card>

      {/* Preview */}
      {preview.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Preview ({preview.length} soal)</h3>
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearPreview}>
                Batal
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={importing}
                className="bg-[#4472C4] hover:bg-[#3861a8]"
                data-testid="import-questions-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Mengimport...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {preview.length} Soal
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          {importing && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-slate-600 mb-1">
                <span>Mengimport soal...</span>
                <span>{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}

          {/* Validation Warnings */}
          {validationErrors.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Peringatan Validasi ({validationErrors.length})
              </h4>
              <ul className="text-sm text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                {validationErrors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">Baris {err.row}</span> - {err.field}: {err.message}
                  </li>
                ))}
                {validationErrors.length > 10 && (
                  <li className="text-amber-600">... dan {validationErrors.length - 10} peringatan lainnya</li>
                )}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Soal</th>
                  <th className="p-2 text-left">Program</th>
                  <th className="p-2 text-left">Mapel</th>
                  <th className="p-2 text-left">Tipe</th>
                  <th className="p-2 text-left">Jawaban</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 20).map((q, idx) => {
                  const rowErrors = validationErrors.filter(e => e.row === idx + 2);
                  const hasError = rowErrors.length > 0;
                  
                  return (
                    <tr key={idx} className={`border-b border-slate-100 ${hasError ? 'bg-amber-50' : ''}`}>
                      <td className="p-2">{idx + 1}</td>
                      <td className="p-2 max-w-xs truncate">{q.text}</td>
                      <td className="p-2">
                        <Badge variant={q.program ? 'secondary' : 'destructive'}>
                          {q.program || 'Kosong'}
                        </Badge>
                      </td>
                      <td className="p-2">{q.mapel || '-'}</td>
                      <td className="p-2">{q.question_type}</td>
                      <td className="p-2">{q.correct_answer || '(weighted)'}</td>
                      <td className="p-2">
                        {hasError ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {rowErrors.length}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            OK
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {preview.length > 20 && (
              <p className="text-center text-slate-500 py-2">
                ... dan {preview.length - 20} soal lainnya
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Hasil Import</h3>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold">{result.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{result.imported}</p>
              <p className="text-sm text-green-600">Berhasil</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{result.errors?.length || 0}</p>
              <p className="text-sm text-red-600">Gagal</p>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="bg-red-50 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Error Details
              </h4>
              <ul className="text-sm text-red-700 space-y-1">
                {result.errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>Baris {err.row}: {err.error}</li>
                ))}
                {result.errors.length > 10 && (
                  <li>... dan {result.errors.length - 10} error lainnya</li>
                )}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
